import { Immutable } from "immer";
import React, { useEffect, useId, useRef, useState } from "react";
import { t } from "ttag";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import {
    sendPhoneVerificationCode,
    verifyPhoneCode,
} from "../../api/phoneVerification";
import { PhoneVerificationChannel } from "../../models/phoneVerification";
import { HttpError } from "../../utils/ajax";
import { trackOtpEvent } from "../../utils/analytics";
import { formatPhoneNumber } from "../../utils/format";

import styles from "./index.module.scss";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

type Phase = "sending" | "input" | "verifying";

type IProps = Immutable<{
    phone: string;
    channel: PhoneVerificationChannel;
    onVerified: () => void;
    onCancel: () => void;
}>;

const getErrorMessage = (err: unknown): string => {
    if (err instanceof HttpError) {
        if (err.status === 429) {
            return t`Too many attempts. Please wait a minute and try again.`;
        }
        if (err.status === 400) {
            return t`That code is invalid or has expired. Please double-check it, or request a new code.`;
        }
    }
    return t`An unknown error occurred. Please try again.`;
};

/**
 * Blocking step that proves the current user controls the given phone number
 * before continuing, by SMS or automated voice call (the channel is chosen by
 * the parent). Mirrors EmailVerificationGate: on mount it requests a one-time
 * code, then the user enters it; if the account has already verified this
 * number (or is exempt) the gate calls onVerified() immediately.
 *
 * Because AWS Notify's pre-approved templates can't carry custom body text,
 * the anti-phishing reminder is shown here on-screen instead of in the message.
 */
export const PhoneVerificationGate = (props: IProps): React.JSX.Element => {
    const isVoice = props.channel === "voice";
    const [phase, setPhase] = useState<Phase>("sending");
    const [code, setCode] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [isSending, setIsSending] = useState(false);
    const inputElem = useRef<HTMLInputElement | null>(null);
    const inputID = useId();
    // Set when the user backs out to edit their info; an in-flight verify /
    // resend continuation must not then submit the application.
    const isCanceled = useRef(false);

    // Tick the resend cooldown timer down once per second.
    useEffect(() => {
        if (resendCooldown <= 0) {
            return;
        }
        const timeoutID = setTimeout(() => {
            setResendCooldown((s) => Math.max(0, s - 1));
        }, 1000);
        return () => {
            clearTimeout(timeoutID);
        };
    }, [resendCooldown]);

    const sendCode = async (isResend = false): Promise<void> => {
        setError(null);
        setIsSending(true);
        try {
            const status = await sendPhoneVerificationCode(
                props.phone,
                props.channel,
            );
            if (status === "already-verified" || status === "not-required") {
                if (isCanceled.current) {
                    return;
                }
                trackOtpEvent(
                    "otp_skipped",
                    "phone_verification_gate",
                    props.channel,
                );
                props.onVerified();
                return;
            }
            trackOtpEvent(
                isResend ? "otp_code_resent" : "otp_code_sent",
                "phone_verification_gate",
                props.channel,
            );
            setCode("");
            setPhase("input");
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
        } catch (err) {
            setPhase("input");
            if (err instanceof HttpError && err.status === 429) {
                setResendCooldown(RESEND_COOLDOWN_SECONDS);
                setError(
                    t`We recently sent a code to this number. Enter that code below, or wait a minute to request a new one.`,
                );
            } else if (err instanceof HttpError && err.status === 400) {
                setError(
                    t`We couldn't send a code to that number. Please go back and double-check it.`,
                );
            } else {
                setError(t`An unknown error occurred. Please try again.`);
            }
        } finally {
            setIsSending(false);
        }
    };

    // Request a code as soon as the gate mounts. The ref guard prevents a
    // double-send under React StrictMode's dev-mode double-effect.
    const didRequestInitialCode = useRef(false);
    useEffect(() => {
        if (didRequestInitialCode.current) {
            return;
        }
        didRequestInitialCode.current = true;
        sendCode();
    }, []);

    useEffect(() => {
        if (phase === "input" && inputElem.current) {
            inputElem.current.focus();
        }
    }, [phase]);

    const onSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        event.preventDefault();
        event.stopPropagation();
        if (code.length !== CODE_LENGTH) {
            setError(t`Please enter the 6-digit code we sent to your phone.`);
            return;
        }
        trackOtpEvent(
            "otp_code_entered",
            "phone_verification_gate",
            props.channel,
        );
        setPhase("verifying");
        setError(null);
        try {
            await verifyPhoneCode(props.phone, code);
            if (isCanceled.current) {
                return;
            }
            trackOtpEvent(
                "otp_verified",
                "phone_verification_gate",
                props.channel,
            );
            props.onVerified();
        } catch (err) {
            if (isCanceled.current) {
                return;
            }
            setPhase("input");
            if (err instanceof HttpError && err.status === 400) {
                trackOtpEvent(
                    "otp_verification_failed",
                    "phone_verification_gate",
                    props.channel,
                );
            }
            setError(getErrorMessage(err));
        }
    };

    const onCodeChange = (event: React.FormEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value
            .replace(/[^0-9]/g, "")
            .slice(0, CODE_LENGTH);
        setCode(value);
    };

    if (phase === "sending") {
        const sendingMessage = isVoice
            ? t`Calling your phone with a verification code…`
            : t`Sending a verification code to your phone…`;
        return (
            <div className={styles.wrapper}>
                <p role="status">{sendingMessage}</p>
            </div>
        );
    }

    const resendLabel =
        resendCooldown > 0
            ? t`Resend code (${resendCooldown} seconds)`
            : t`Resend code`;
    const displayPhone = formatPhoneNumber(props.phone);
    const instructions = isVoice
        ? richT(
              t`To protect against fraudulent applications, we're calling <strong>${displayPhone}</strong> with a 6-digit code. Enter it below to continue.`,
          )
        : richT(
              t`To protect against fraudulent applications, we've texted a 6-digit code to <strong>${displayPhone}</strong>. Enter it below to continue.`,
          );
    const hint = isVoice
        ? t`Didn't get the call? Request a new code, or choose a different method.`
        : t`Didn't get the text? Request a new code, or choose a different method.`;
    return (
        <form className={styles.wrapper} onSubmit={onSubmit} noValidate={true}>
            <h3 className={styles.heading}>{t`Verify your phone number`}</h3>
            <p className={styles.instructions}>{instructions}</p>
            <label className={styles.label} htmlFor={inputID}>
                {t`Verification code`}
            </label>
            <input
                ref={inputElem}
                id={inputID}
                className={styles.codeInput}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                maxLength={CODE_LENGTH}
                value={code}
                disabled={phase === "verifying"}
                onChange={onCodeChange}
            />
            {error && (
                <p className={styles.error} role="alert">
                    {error}
                </p>
            )}
            <p className={styles.antiPhishing}>
                {t`We'll never call, text, or email you to ask for this code. Don't share it with anyone.`}
            </p>
            <div className={styles.actions}>
                <button
                    type="submit"
                    className="button"
                    disabled={
                        phase === "verifying" || code.length !== CODE_LENGTH
                    }
                >
                    {phase === "verifying" ? t`Verifying…` : t`Continue`}
                </button>
            </div>
            <div className={styles.secondaryActions}>
                <button
                    type="button"
                    className={styles.linkButton}
                    disabled={
                        resendCooldown > 0 || isSending || phase === "verifying"
                    }
                    onClick={() => {
                        sendCode(true);
                    }}
                >
                    {resendLabel}
                </button>
                <button
                    type="button"
                    className={styles.linkButton}
                    disabled={phase === "verifying"}
                    onClick={() => {
                        isCanceled.current = true;
                        props.onCancel();
                    }}
                >
                    {t`Edit my information`}
                </button>
            </div>
            <p className={styles.hint}>{hint}</p>
        </form>
    );
};
